import type { Transform, ITransformPipeline } from "./types";

/**
 * Creates a transform pipeline that can process values through a series of transform functions
 *
 * @template T - The type of value being transformed
 * @template C - The type of context object passed to transforms (void if no context needed)
 *
 * @param initial - Initial array of transform functions to populate the pipeline
 *
 * @example
 * ```typescript
 * // Create a string transform pipeline
 * const pipeline = createTransformPipeline<string>();
 *
 * // Register transforms
 * pipeline.register(str => str.trim());
 * pipeline.register(str => str.toUpperCase());
 *
 * // Apply transforms
 * const result = pipeline.apply("  hello  "); // "HELLO"
 * ```
 *
 * @example
 * ```typescript
 * // Pipeline with context
 * type Context = { prefix: string };
 * const pipeline = createTransformPipeline<string, Context>();
 *
 * pipeline.register((str, ctx) => ctx.prefix + str);
 * const result = pipeline.apply("world", { prefix: "hello " }); // "hello world"
 * ```
 */
export function createTransformPipeline<T, C = unknown>(
    initial: readonly Transform<T, C>[] = [],
): ITransformPipeline<T, C> {
    const registry: Transform<T, C>[] = [...initial];

    return {
        register(fn) {
            registry.push(fn);
        },
        apply(value, ctx) {
            return registry.reduce((out, fn) => fn(out, ctx), value);
        },
    };
}
